import "./App.css";

import { ApmBase } from "@elastic/apm-rum";
import { Box, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import UseAuth from "shared/common/hooks/UseAuth";
import { getUserData, tokenType } from "shared/common/services/Login";
import { initViewerOptions, Manager } from "shared/common/services/manager";
import { defaultStore } from "shared/common/stores/defaultStore";
import EditLayerSettings from "shared/common/utils/layers/EditLayerSettings";
import Cockpit from "shared/ui/cockpit/Cockpit";
import Map from "shared/ui/map/Map";
import HomePage from "shared/ui/pages/HomePage";
import Toolbar from "shared/ui/toolbar/Toolbar";
import AxisVisibilityPanelButton from "shared/ui/toolbar/tools/axisPanel/AxisPanelButton";
import Cursor from "shared/ui/toolbar/tools/cursor/Cursor";
import FeatureInfo from "shared/ui/toolbar/tools/featureInfo/FeatureInfo";
import ImageInfo from "shared/ui/toolbar/tools/imageInfo/ImageInfo";
import Edit from "shared/ui/toolbar/tools/layertreeAndEdit/Edit";
import Layer from "shared/ui/toolbar/tools/layertreeAndEdit/Layer";
import Measurements from "shared/ui/toolbar/tools/measurements/Measurements";
import PointCloudExport from "shared/ui/toolbar/tools/pointCloudExport/PointCloudExport";
import PointCloudExportButton from "shared/ui/toolbar/tools/pointCloudExport/PointCloudExportButton";
import Share from "shared/ui/toolbar/tools/share/Share";
import ReportAnon from "shared/ui/topbar/items/help/ReportAnon";
import Topbar from "shared/ui/topbar/Topbar";

interface props {
  apm: ApmBase;
  manager: Manager;
  onLoaded: () => void;
  userContext?: {
    id?: string;
    username?: string;
    email?: string;
  };
}

interface options {
  project_uid: string;
  start_position?: { easting: number; northing: number; epsg?: number };
  start_campaign?: { id: string };
  map_expand?: boolean;
  show_cockpit?: boolean;
  show_mapWindow?: boolean;
  show_toolbar?: boolean;
  show_topbar?: boolean;
}

function App({ apm, manager, onLoaded, userContext }: props) {
  const useDefaultStore = defaultStore();
  const { changeApm, clientConfig, editLayerSettings, changeProjectConfig } =
    useDefaultStore;

  const role = "api";

  const { userData, setUserData, setTokens } = UseAuth();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();

  const [project_uid, setProjectUID] = useState("");
  const [start_position, setStartPosition] = useState<
    { easting: number; northing: number; epsg?: number } | undefined
  >();
  const [start_campaign, setStartCampaign] = useState<
    { id: string } | undefined
  >();
  const [map_expand, setMapExpand] = useState(false);
  const [show_cockpit, setCockpit] = useState(true);
  const [show_mapWindow, setMapwindow] = useState(true);
  const [show_toolbar, setShowToolbar] = useState(false);
  const [show_topbar, setTopbar] = useState(true);

  const [authMode, setAuthMode] = useState<"edit" | "view">("view");

  useEffect(() => {
    if (!userData) return;
    let permission: "view" | "edit" | undefined;
    if (userData.scope.includes("permission/edit")) {
      permission = "edit";
    } else if (userData.scope.includes("permission/view")) {
      permission = "view";
    }

    if (!permission) {
      showMessage(t("alert_error.no_permission"), "error");
      return;
    }
    setAuthMode(permission);
  }, [userData, showMessage, t]);

  const setOptions = (options: options) => {
    changeProjectConfig(undefined);
    setProjectUID(options.project_uid);
    setStartPosition(options.start_position);
    setStartCampaign(options.start_campaign);
    setMapExpand(options.map_expand ?? false);
    setCockpit(options.show_cockpit ?? true);
    setMapwindow(options.show_mapWindow ?? true);
    setShowToolbar(options.show_toolbar ?? false);
    setTopbar(options.show_topbar ?? true);
  };

  useEffect(() => {
    let current_tokens: tokenType;
    manager.on("tokenset", (token) => {
      current_tokens = token;
      setTokens(token);
      const userData = getUserData(token);
      if (!userData.user_familyname && userContext?.username)
        userData.user_familyname = userContext.username;
      if (!userData.usermail && userContext?.email)
        userData.usermail = userContext.email;
      setUserData(userData);
    });
    const oninit = (options: initViewerOptions) => {
      setOptions(options);
      const userData = getUserData(current_tokens);
      if (!userData.user_familyname && userContext?.username)
        userData.user_familyname = userContext.username;
      if (!userData.usermail && userContext?.email)
        userData.usermail = userContext.email;
      setUserData(userData);
    };
    manager.on("viewinitialized", oninit);
    return () => {
      manager.off("viewinitialized", oninit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager]);

  useEffect(() => {
    changeApm(apm);
    onLoaded();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {project_uid && (
        <HomePage
          manager={manager}
          role={role}
          project_uid={project_uid}
          start_position={start_position}
          start_campaign={start_campaign}
          map_expand={map_expand}
          show_cockpit={show_cockpit}
          show_mapWindow={show_mapWindow}
          show_toolbar={show_toolbar}
          show_topbar={show_topbar}
          authMode={authMode}
        >
          {show_topbar ? <Topbar role={role} /> : <></>}

          <Box
            id="toolbar"
            position={"absolute"}
            height={"70%"}
            top="15%"
            left="0"
            zIndex={11}
            sx={{ pointerEvents: "none" }}
          >
            {authMode === "view" && show_toolbar && (
              <Toolbar>
                <Layer authMode={authMode} />
                <FeatureInfo />
                <AxisVisibilityPanelButton />
                <Divider sx={{ backgroundColor: "white", height: "1px" }} />
                <ImageInfo />
              </Toolbar>
            )}

            {authMode === "edit" && show_toolbar && (
              <Toolbar>
                <Layer authMode={authMode} />
                <FeatureInfo />
                <AxisVisibilityPanelButton />
                <Divider sx={{ backgroundColor: "white", height: "1px" }} />
                <Cursor />
                <Measurements />
                <Edit authMode={authMode} />
                <Divider sx={{ backgroundColor: "white", height: "1px" }} />
                <ImageInfo />
                <Share role={role} />
                <PointCloudExportButton />
              </Toolbar>
            )}
            {clientConfig?.anon_project && (
              <ReportAnon anonProject={clientConfig.anon_project} />
            )}
            {editLayerSettings && <EditLayerSettings />}
            <PointCloudExport />
          </Box>
          <Box
            zIndex={11}
            height="calc(85% - 104px)"
            position={"absolute"}
            top="15%"
            right="0"
            sx={{
              pointerEvents: "none",
            }}
          >
            <Map />
            {/* Needs to be loaded, even if it is not shown. Otherwise the point cloud export will not work properly. */}
          </Box>
          {show_cockpit ? <Cockpit /> : <></>}
        </HomePage>
      )}
    </>
  );
}

export default App;
